/**
 * Badge values are open ended, so colours are assigned per column: distinct
 * values claim palette slots in order of first appearance. The hash is the
 * fallback for values the column has not seen yet, such as a draft.
 */
const BADGE_PALETTE = [
  "border-transparent bg-primary/10 text-primary",
  "border-transparent bg-chart-1/15 text-chart-1",
  "border-transparent bg-chart-2/15 text-chart-2",
  "border-transparent bg-rose-500/10 text-rose-700 dark:text-rose-300",
  "border-transparent bg-indigo-500/12 text-indigo-700 dark:text-indigo-300",
  "border-transparent bg-amber-500/15 text-amber-900 dark:text-amber-100",
  "border-transparent bg-emerald-500/12 text-emerald-700 dark:text-emerald-300",
  "border-transparent bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300",
  "border-transparent bg-chart-3/12 text-chart-3 dark:bg-chart-3/40 dark:text-foreground",
  "border-transparent bg-lime-500/15 text-lime-800 dark:text-lime-300",
  "border-transparent bg-sky-500/12 text-sky-700 dark:text-sky-300",
  "border-transparent bg-slate-500/12 text-slate-700 dark:text-slate-300",
];

export type BadgeColorEntry = { className: string; display: string };

// Normalized value to colour and first-seen spelling, per badge column.
export type BadgeColorMap = Map<string, BadgeColorEntry>;

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function badgeClassAt(index: number): string {
  return BADGE_PALETTE[index % BADGE_PALETTE.length] ?? "";
}

export function buildBadgeColorMap(values: string[]): BadgeColorMap {
  const map: BadgeColorMap = new Map();
  for (const value of values) {
    const key = normalize(value);
    if (!key || map.has(key)) continue;
    map.set(key, {
      className: badgeClassAt(map.size),
      display: value.trim(),
    });
  }
  return map;
}

export function badgeClassIn(
  map: BadgeColorMap | undefined,
  value: string,
): string {
  return map?.get(normalize(value))?.className ?? badgeClassFor(value);
}

export function badgeClassFor(value: string): string {
  const normalized = normalize(value);
  let hash = 5381;
  for (let i = 0; i < normalized.length; i++) {
    hash = (hash * 33) ^ normalized.charCodeAt(i);
  }
  return badgeClassAt(Math.abs(hash));
}
